import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShieldCheck, CalendarClock, Box, Snowflake, Layers } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const EQUIPMENT = [
  {
    id: "dryvan",
    icon: Box,
    name: "Dry Van",
    text: "Standard enclosed trailers for boxed, palletized and non-perishable freight — the backbone of the program and the highest share of scheduled loads.",
  },
  {
    id: "reefer",
    icon: Snowflake,
    name: "Reefer",
    text: "Temperature-controlled trailers for chilled and frozen goods, with continuous monitoring from pickup to delivery.",
  },
  {
    id: "flatbed",
    icon: Layers,
    name: "Flatbed",
    text: "Open-deck trailers for oversized, irregular and construction freight, with full tarping and securement support.",
  },
];

function Countdown() {
  const [target] = useState(() => {
    const now = new Date();
    let t = new Date(now.getFullYear(), 10, 1, 0, 0, 0);
    if (now > t) t = new Date(now.getFullYear() + 1, 10, 1, 0, 0, 0);
    return t;
  });
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const diff = Math.max(0, target - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);

  const cells = [
    { v: d, l: "Days" },
    { v: h, l: "Hours" },
    { v: m, l: "Min" },
    { v: s, l: "Sec" },
  ];

  return (
    <div className="flex gap-3">
      {cells.map((c) => (
        <div key={c.l} className="rounded-xl border border-[#0066FF]/30 bg-[#070F1E]/80 px-3 py-2 text-center sm:px-4">
          <p className="font-mono text-xl font-extrabold text-white sm:text-2xl">{String(c.v).padStart(2, "0")}</p>
          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{c.l}</p>
        </div>
      ))}
    </div>
  );
}

export default function Project() {
  const [active, setActive] = useState("dryvan");
  const Active = EQUIPMENT.find((e) => e.id === active);

  return (
    <section id="project" className="relative overflow-hidden bg-[#0B172A] py-24 lg:py-32">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[800px] -translate-x-1/2 rounded-full bg-[#0066FF]/15 blur-[130px]" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Eyebrow>Upcoming Project</Eyebrow>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="max-w-2xl font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              The 20,000-Load Initiative
            </h2>
            <div data-testid="project-countdown">
              <Countdown />
            </div>
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* rate spotlight */}
          <Reveal className="lg:col-span-7" delay={0.05}>
            <div className="relative h-full overflow-hidden rounded-3xl border border-[#FF3B30]/25 bg-gradient-to-br from-[#070F1E] via-[#0B172A] to-[#101c33] p-8 sm:p-10">
              <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-[#FF3B30]/10 blur-[80px]" />
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-2 rounded-full border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-4 py-1.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30]">
                  <CalendarClock className="h-3.5 w-3.5" /> Starting November 1
                </span>
                <span className="flex items-center gap-2 rounded-full border border-[#0066FF]/40 bg-[#0066FF]/10 px-4 py-1.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#00F0FF]">
                  <ShieldCheck className="h-3.5 w-3.5" /> Exclusively for Approved Carriers
                </span>
              </div>

              <p className="mt-10 font-mono text-sm uppercase tracking-[0.3em] text-slate-400">Up to</p>
              <div className="relative mt-2 inline-block" data-testid="project-rate-display">
                <span className="ping-slow absolute inset-0 rounded-full border-2 border-[#FF3B30]/50" />
                <p className="relative px-4 py-2 font-mono text-6xl font-black leading-none text-[#FF3B30] sm:text-7xl lg:text-8xl">
                  $4.20
                </p>
              </div>
              <p className="mt-3 pl-1 font-mono text-sm uppercase tracking-[0.3em] text-slate-400">Per mile</p>

              <div className="mt-10 grid grid-cols-3 gap-4 border-t border-white/10 pt-8">
                <div>
                  <p className="font-mono text-2xl font-extrabold text-white sm:text-3xl">20,000</p>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">Total Loads</p>
                </div>
                <div>
                  <p className="font-mono text-2xl font-extrabold text-white sm:text-3xl">3</p>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">Equipment Classes</p>
                </div>
                <div>
                  <p className="font-mono text-2xl font-extrabold text-[#FF3B30] sm:text-3xl">$4.20</p>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">Top Rate / Mile</p>
                </div>
              </div>
            </div>
          </Reveal>

          {/* equipment tabs */}
          <Reveal className="lg:col-span-5" delay={0.12}>
            <div className="flex h-full flex-col rounded-3xl border border-[#0066FF]/20 bg-[#070F1E] p-8 sm:p-10">
              <h3 className="font-display text-xl font-bold text-white">Equipment in demand</h3>
              <div className="mt-6 flex flex-wrap gap-2">
                {EQUIPMENT.map((eq) => (
                  <button
                    key={eq.id}
                    data-testid={`equipment-tab-${eq.id}`}
                    onClick={() => setActive(eq.id)}
                    className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                      active === eq.id
                        ? "bg-[#0066FF] text-white shadow-[0_0_25px_rgba(0,102,255,0.45)]"
                        : "border border-slate-700 text-slate-400 hover:border-[#0066FF] hover:text-white"
                    }`}
                  >
                    <eq.icon className="h-4 w-4" />
                    {eq.name}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={Active.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3 }}
                  className="mt-8 flex-1"
                >
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#00F0FF]">{Active.name}</p>
                  <p className="mt-3 text-base leading-relaxed text-slate-400">{Active.text}</p>
                </motion.div>
              </AnimatePresence>

              <div className="relative mt-8 h-36 overflow-hidden rounded-2xl border border-[#0066FF]/20">
                <img
                  src="https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxmcmVpZ2h0JTIwdHJ1Y2slMjBoaWdod2F5JTIwbG9naXN0aWNzfGVufDB8fHx8MTc5MDYwNjYyMXww&ixlib=rb-4.1.0&q=85"
                  alt="Freight truck on highway"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070F1E]/90 to-transparent" />
                <p className="absolute bottom-3 left-4 font-mono text-xs uppercase tracking-[0.2em] text-slate-300">
                  National capacity — all 48 contiguous states
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
